import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Send, Loader2, Mail, MapPin, Linkedin } from 'lucide-react';
import ArtStationIcon from './ui/icons/ArtStationIcon';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill name, email and message.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success('Message sent. I’ll be in touch within 48h.');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(', ') : (detail || 'Failed to send. Try again.');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex items-center gap-4 mb-10"
        >
          <Mail className="text-[#e58a59]" size={20} />
          <span className="text-xs uppercase tracking-[0.3em] text-zinc-400">Contact</span>
          <div className="flex-1 h-px bg-white/10" />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="font-display text-white font-black text-6xl sm:text-7xl lg:text-[10rem] leading-[0.95] tracking-tighter mb-12"
          data-testid="contact-heading"
        >
          LET&rsquo;S
          <br />
          <span className="text-[#e58a59] italic font-light">talk.</span>
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-10">
            <p className="text-zinc-400 text-base lg:text-lg leading-relaxed max-w-md">
              Open to junior positions, internships and part-time roles in game
              design, character art, and gameplay engineering. Drop a brief — I
              read every message myself.
            </p>

            <div className="space-y-5 border-t border-white/10 pt-8">
              <Info
                icon={Mail}
                label="Email"
                value="stephanos.papas@outlook.com"
                href="mailto:stephanos.papas@outlook.com"
                testId="contact-info-email"
              />
              <Info
                icon={Linkedin}
                label="LinkedIn"
                value="/in/stephanos-papas"
                href="https://www.linkedin.com/in/stephanos-papas-780ab7213/"
                testId="contact-info-linkedin"
              />
              <Info
                icon={MapPin}
                label="Based in"
                value="Hamburg, Germany · Open to relocation"
                testId="contact-info-location"
              />
            </div>
          </div>

          <form
            onSubmit={submit}
            className="lg:col-span-7 p-8 lg:p-10 rounded-2xl border border-white/10 bg-[#101012] space-y-5"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Name" id="name">
                <Input
                  id="name" name="name" value={form.name} onChange={onChange}
                  placeholder="Your name"
                  className="bg-[#0c0c0e] border-white/10 text-white h-12"
                  data-testid="contact-name-input"
                />
              </Field>
              <Field label="Email" id="email">
                <Input
                  id="email" name="email" type="email" value={form.email} onChange={onChange}
                  placeholder="you@studio.com"
                  className="bg-[#0c0c0e] border-white/10 text-white h-12"
                  data-testid="contact-email-input"
                />
              </Field>
            </div>
            <Field label="Subject (optional)" id="subject">
              <Input
                id="subject" name="subject" value={form.subject} onChange={onChange}
                placeholder="Project, role, or hello"
                className="bg-[#0c0c0e] border-white/10 text-white h-12"
                data-testid="contact-subject-input"
              />
            </Field>
            <Field label="Message" id="message">
              <Textarea
                id="message" name="message" value={form.message} onChange={onChange}
                placeholder="Tell me about the project…"
                rows={6}
                className="bg-[#0c0c0e] border-white/10 text-white"
                data-testid="contact-message-input"
              />
            </Field>

            <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
              <span className="text-xs text-zinc-500">
                Replies within 48 hours · No spam, ever.
              </span>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#e58a59] hover:bg-[#f0a075] text-black font-semibold rounded-full px-7 h-12 inline-flex items-center gap-2"
                data-testid="contact-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Send size={16} /> Send Message
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, id, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-[11px] uppercase tracking-[0.22em] text-zinc-400 mb-2 block">
        {label}
      </Label>
      {children}
    </div>
  );
}

function Info({ icon: Icon, label, value, href, testId }) {
  const inner = (
    <>
      <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center bg-[#121214] shrink-0">
        <Icon size={16} className="text-[#e58a59]" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</div>
        <div className="text-white text-sm mt-1">{value}</div>
      </div>
    </>
  );
  if (href) {
    return (
      <a
        href={href}
        target={href.startsWith('http') ? '_blank' : undefined}
        rel="noreferrer"
        className="flex items-start gap-4 group hover:text-[#e58a59] transition-colors"
        data-testid={testId}
      >
        {inner}
      </a>
    );
  }
  return (
    <div className="flex items-start gap-4" data-testid={testId}>
      {inner}
    </div>
  );
}
